<div class="flex flex-col w-full h-full p-6 md:p-8 bg-gray-50 dark:bg-gray-900" [ngSwitch]="currentStep">

    <!-- Step 1: Report Selection -->
    <ng-container *ngSwitchCase="'selection'">
        <div class="w-full max-w-4xl mx-auto" @fadeIn>
            <div class="text-center">
                <h1 class="text-3xl md:text-4xl font-extrabold tracking-tight">Report Generator</h1>
                <p class="mt-2 text-lg text-secondary">Please select a report to continue.</p>
            </div>
            <mat-progress-bar *ngIf="isLoading" mode="indeterminate" class="mt-8"></mat-progress-bar>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                <div *ngFor="let report of reportConfigs"
                     (click)="selectReport(report)"
                     class="group bg-card rounded-2xl shadow-lg p-6 flex flex-col items-center text-center cursor-pointer
                            hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ease-in-out
                            border border-transparent hover:border-primary">
                    <div class="bg-primary/10 rounded-full p-4">
                        <mat-icon class="text-primary scale-150">assessment</mat-icon>
                    </div>
                    <h2 class="font-bold text-lg mt-4">{{ report.title }}</h2>
                    <p class="text-secondary text-sm mt-1">Type: {{ report.type | titlecase }}</p>
                </div>
            </div>
        </div>
    </ng-container>

    <!-- Step 2: Configuration Form -->
    <ng-container *ngSwitchCase="'form'">
        <div class="w-full max-w-4xl mx-auto" @fadeIn *ngIf="selectedReportConfig">
            <div class="text-center">
                <h1 class="text-3xl md:text-4xl font-extrabold tracking-tight">{{ selectedReportConfig.title }}</h1>
                <p class="mt-2 text-lg text-secondary">Use the filters below to generate your report.</p>
            </div>

            <div class="mt-8 p-8 bg-card rounded-2xl shadow-lg">
                <form [formGroup]="reportForm" (ngSubmit)="generateReport()">
                    <div class="grid grid-cols-1 md:grid-cols-6 gap-x-6 gap-y-8">
                        <ng-container *ngFor="let param of selectedReportConfig.params">
                            <div [ngClass]="{
                                'md:col-span-3': param.type === 'select',
                                'md:col-span-6': param.type === 'text',
                                'md:col-span-2': param.type === 'search'
                            }">
                                <mat-form-field class="w-full" *ngIf="param.type === 'select'">
                                    <mat-label>{{ param.title }}</mat-label>
                                    <mat-select [formControlName]="param.name">
                                        <mat-option *ngFor="let option of paramData[param.name]"
                                                    [value]="option[param.id_column]">
                                            {{ option[param.display_column] }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                                <mat-form-field class="w-full" *ngIf="param.type === 'text'">
                                    <mat-label>{{ param.title }}</mat-label>
                                    <input matInput [formControlName]="param.name"
                                           [placeholder]="'Enter a ' + param.title.toLowerCase()">
                                </mat-form-field>
                                <mat-form-field class="w-full" *ngIf="param.type === 'search'">
                                    <mat-label>{{ param.title }}</mat-label>
                                    <input type="text" matInput [formControlName]="param.name"
                                           [matAutocomplete]="auto"
                                           [placeholder]="'Search for a ' + param.title.toLowerCase()">
                                    <mat-autocomplete #auto="matAutocomplete" [displayWith]="displaySearchOption">
                                        <mat-option *ngFor="let option of filteredOptions[param.name] | async"
                                                    [value]="option">
                                            {{ option[param.display_column] }}
                                        </mat-option>
                                    </mat-autocomplete>
                                </mat-form-field>
                            </div>
                        </ng-container>
                    </div>

                    <div class="flex justify-between items-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <button mat-stroked-button type="button" (click)="resetWizard()">
                            <mat-icon class="mr-2">arrow_back</mat-icon>
                            <span>Change Report</span>
                        </button>
                        <button mat-flat-button color="primary" type="submit"
                                [disabled]="reportForm.invalid || isLoading">
                            <mat-icon class="mr-2">dynamic_feed</mat-icon>
                            <span>Generate Report</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </ng-container>

    <!-- Step 3: Report Results -->
    <ng-container *ngSwitchCase="'results'">
        <div class="flex flex-col w-full h-full" @fadeIn>
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <div>
                    <h2 class="text-3xl font-extrabold tracking-tight">{{ selectedReportConfig.title }} Results</h2>
                    <div class="text-secondary font-medium">
                        <span *ngIf="!isDownloading; else downloadMessage">
                            {{ dataSource?.filteredData.length || 0 }} of {{ dataSource?.data.length || 0 }} records found
                        </span>
                        <ng-template #downloadMessage>
                            <span class="flex items-center text-accent font-semibold">
                                <mat-icon class="mr-2 animate-spin">sync</mat-icon>
                                Preparing download...
                            </span>
                        </ng-template>
                    </div>
                </div>

                <div class="flex items-center justify-end flex-shrink-0 space-x-4">
                    <mat-form-field class="w-full sm:w-60">
                        <mat-label>Filter</mat-label>
                        <input matInput (keyup)="applyFilter($event)" placeholder="Search..." #input [disabled]="isDownloading">
                        <mat-icon matPrefix class="mr-2">search</mat-icon>
                    </mat-form-field>
                    <button mat-flat-button color="primary" [matMenuTriggerFor]="exportMenu" [disabled]="isDownloading">
                        <mat-icon>save_alt</mat-icon>
                        <span class="ml-2 hidden md:inline">Export</span>
                    </button>
                    <mat-menu #exportMenu="matMenu">
                        <button mat-menu-item (click)="copyTableToClipboard()">
                            <mat-icon>content_copy</mat-icon>
                            <span>Copy to Clipboard</span>
                        </button>
                        <button mat-menu-item (click)="downloadReport('csv')">
                            <mat-icon>description</mat-icon>
                            <span>Download CSV</span>
                        </button>
                        <button mat-menu-item (click)="downloadReport('tsv')">
                            <mat-icon>description</mat-icon>
                            <span>Download TSV</span>
                        </button>
                        <button mat-menu-item (click)="downloadReport('txt')">
                            <mat-icon>article</mat-icon>
                            <span>Download TXT</span>
                        </button>
                    </mat-menu>
                    <button mat-stroked-button (click)="goBackToForm()" [disabled]="isDownloading">
                        <mat-icon>edit</mat-icon>
                        <span class="ml-2 hidden md:inline">Edit</span>
                    </button>
                    <button mat-stroked-button (click)="resetWizard()" [disabled]="isDownloading">
                        <mat-icon>add_circle_outline</mat-icon>
                        <span class="ml-2 hidden md:inline">New Report</span>
                    </button>
                </div>
            </div>

            <mat-progress-bar *ngIf="isLoading" mode="indeterminate" class="mb-4"></mat-progress-bar>

            <div class="flex-auto overflow-hidden rounded-2xl shadow-lg bg-card border dark:border-gray-700">
                <div class="overflow-auto h-full">
                    <table mat-table [dataSource]="dataSource" matSort class="w-full">
                        <ng-container *ngFor="let col of displayedColumns" [matColumnDef]="col">
                            <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-3 text-left text-sm font-semibold tracking-wider">
                                {{ col | titlecase }}
                            </th>
                            <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm">
                                {{ element[col] }}
                            </td>
                        </ng-container>
                        <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true" class="bg-gray-50 dark:bg-gray-800/60"></tr>
                        <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="border-b border-gray-200 dark:border-gray-700 last:border-0 hover:bg-gray-100 dark:hover:bg-gray-700/40 cursor-pointer transition-colors duration-200"></tr>
                        <tr class="mat-row" *matNoDataRow>
                            <td class="mat-cell text-center p-12 text-secondary" [attr.colspan]="displayedColumns.length">
                                No data matching the filter "{{ input.value }}"
                            </td>
                        </tr>
                    </table>
                </div>
            </div>
            <mat-paginator [pageSizeOptions]="[10, 25, 100]" class="mt-4 -mx-2 bg-transparent"></mat-paginator>
        </div>
    </ng-container>
</div>
